import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { 
  Menu, 
  X, 
  User, 
  LogOut, 
  LayoutDashboard,
  Calendar,
  Settings,
  Shield,
  Sun,
  Moon,
  Home,
  Search,
  CreditCard
} from 'lucide-react';

// Language names for display
const languageNames = {
  fr: 'Français',
  en: 'English', 
  de: 'Deutsch',
  bg: 'Български',
  es: 'Español',
  pt: 'Português',
  ar: 'العربية',
  it: 'Italiano',
  lb: 'Lëtzebuergesch'
};

// Circular flag images for each language
const languageFlagImages = {
  fr: 'https://flagcdn.com/w40/fr.png',
  en: 'https://flagcdn.com/w40/gb.png',
  de: 'https://flagcdn.com/w40/de.png',
  bg: 'https://flagcdn.com/w40/bg.png',
  es: 'https://flagcdn.com/w40/es.png',
  pt: 'https://flagcdn.com/w40/pt.png',
  ar: 'https://flagcdn.com/w40/sa.png',
  it: 'https://flagcdn.com/w40/it.png',
  lb: 'https://flagcdn.com/w40/lu.png'
};

// Circular Flag Component
const CircularFlag = ({ code, size = 24, className = '' }) => (
  <div 
    className={`rounded-full overflow-hidden border-2 border-white shadow-sm flex-shrink-0 ${className}`}
    style={{ width: size, height: size }}
  >
    <img 
      src={languageFlagImages[code]} 
      alt={languageNames[code]}
      className="w-full h-full object-cover"
      onError={(e) => {
        e.target.style.display = 'none';
      }}
    />
  </div>
);

export const Header = () => {
  const { locale, changeLanguage, supportedLocales, isRTL } = useLanguage();
  const { user, logout, isAuthenticated, isSuperAdmin, isCountryAdmin, isProfessional } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardPath = () => {
    if (isSuperAdmin) return '/admin';
    if (isCountryAdmin) return '/country-admin';
    if (isProfessional) return '/professional';
    return '/dashboard';
  };

  // Simple translation function - fallback to French then key
  const t = (key) => {
    const translations = {
      'nav_home': { fr: 'Accueil', en: 'Home', de: 'Startseite', es: 'Inicio', pt: 'Início', ar: 'الرئيسية', it: 'Home', lb: 'Heem', bg: 'Начало', nl: 'Home', tr: 'Ana sayfa' },
      'nav_professionals': { fr: 'Rechercher', en: 'Search', de: 'Suchen', es: 'Buscar', pt: 'Pesquisar', ar: 'بحث', it: 'Cerca', lb: 'Sichen', bg: 'Търсене', nl: 'Zoeken', tr: 'Ara' },
      'nav_pricing': { fr: 'Tarifs', en: 'Pricing', de: 'Preise', es: 'Precios', pt: 'Preços', ar: 'الأسعار', it: 'Prezzi', lb: 'Präisser', bg: 'Цени', nl: 'Prijzen', tr: 'Fiyatlar' },
      'nav_login': { fr: 'Connexion', en: 'Login', de: 'Anmelden', es: 'Entrar', pt: 'Entrar', ar: 'دخول', it: 'Accedi', lb: 'Aloggen', bg: 'Вход', nl: 'Inloggen', tr: 'Giriş' },
      'nav_register': { fr: 'Inscription', en: 'Sign up', de: 'Registrieren', es: 'Registrarse', pt: 'Registar', ar: 'تسجيل', it: 'Registrati', lb: 'Umellen', bg: 'Регистрация', nl: 'Registreren', tr: 'Kayıt' },
      'nav_blog': { fr: 'Blog', en: 'Blog', de: 'Blog', es: 'Blog', pt: 'Blog', ar: 'مدونة', it: 'Blog', lb: 'Blog', bg: 'Блог', nl: 'Blog', tr: 'Blog' },
      'nav_dashboard': { fr: 'Tableau de bord', en: 'Dashboard', de: 'Dashboard', es: 'Panel', pt: 'Painel', ar: 'لوحة التحكم', it: 'Dashboard', lb: 'Dashboard', bg: 'Табло', nl: 'Dashboard', tr: 'Panel' },
      'nav_profile': { fr: 'Mon profil', en: 'My profile', de: 'Mein Profil', es: 'Mi perfil', pt: 'Meu perfil', ar: 'ملفي', it: 'Profilo', lb: 'Mäi Profil', bg: 'Моят профил', nl: 'Mijn profiel', tr: 'Profilim' },
      'nav_logout': { fr: 'Déconnexion', en: 'Logout', de: 'Abmelden', es: 'Salir', pt: 'Sair', ar: 'خروج', it: 'Esci', lb: 'Ausloggen', bg: 'Изход', nl: 'Uitloggen', tr: 'Çıkış' },
      'language': { fr: 'Langue', en: 'Language', de: 'Sprache', es: 'Idioma', pt: 'Idioma', ar: 'اللغة', it: 'Lingua', lb: 'Sprooch', bg: 'Език', nl: 'Taal', tr: 'Dil' },
      'dashboard_settings': { fr: 'Paramètres', en: 'Settings', de: 'Einstellungen', es: 'Ajustes', pt: 'Configurações', ar: 'الإعدادات', it: 'Impostazioni', lb: 'Astellungen', bg: 'Настройки', nl: 'Instellingen', tr: 'Ayarlar' }
    };
    return translations[key]?.[locale] || translations[key]?.['fr'] || key;
  };

  return (
    <header className="sticky top-0 z-50 glass border-b border-slate-200/50" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-900">drbooking</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className={`hidden md:flex items-center gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Link to="/" className="text-slate-600 hover:text-primary transition-colors" data-testid="nav-home">
              {t('nav_home')}
            </Link>
            <Link to="/professionals" className="text-slate-600 hover:text-primary transition-colors" data-testid="nav-professionals">
              {t('nav_professionals')}
            </Link>
            <Link to="/pricing" className="text-slate-600 hover:text-primary transition-colors" data-testid="nav-pricing">
              {t('nav_pricing')}
            </Link>
            <Link to="/blog" className="text-slate-600 hover:text-primary transition-colors" data-testid="nav-blog">
              {t('nav_blog')}
            </Link>
            {!user && (
              <Link to="/login" className="text-blue-600 font-semibold hover:text-blue-700 transition-colors" data-testid="nav-login">
                {t('nav_login')}
              </Link>
            )}
            {isSuperAdmin && (
              <Link to="/admin" className="text-amber-600 hover:text-amber-500 transition-colors flex items-center gap-1" data-testid="nav-admin">
                <Shield className="w-4 h-4" />
                Admin
              </Link>
            )}
          </nav>

          {/* Actions */}
          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Dark Mode Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleDarkMode}
              className="p-2"
              data-testid="dark-mode-toggle"
              title={isDarkMode ? 'Mode clair' : 'Mode sombre'}
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5 text-yellow-400" />
              ) : (
                <Moon className="w-5 h-5 text-slate-600" />
              )}
            </Button>

            {/* Language Selector with Circular Flags */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 px-2" data-testid="language-selector">
                  <CircularFlag code={locale} size={28} />
                  <span className="hidden sm:inline text-sm font-medium">{languageNames[locale]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="w-48">
                {Object.entries(languageNames).map(([code, name]) => (
                  <DropdownMenuItem
                    key={code}
                    onClick={() => changeLanguage(code)}
                    className={`flex items-center gap-3 cursor-pointer ${locale === code ? 'bg-primary/10 text-primary' : ''}`}
                    data-testid={`lang-${code}`}
                  >
                    <CircularFlag code={code} size={24} />
                    <span>{name}</span>
                    {locale === code && (
                      <span className="ml-auto text-primary">✓</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="user-menu">
                    <User className="w-5 h-5" />
                    <span className="hidden sm:inline">{user?.first_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="w-48">
                  <DropdownMenuItem onClick={() => navigate(getDashboardPath())} data-testid="goto-dashboard">
                    <LayoutDashboard className="w-4 h-4 me-2" />
                    {t('nav_dashboard')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="goto-settings">
                    <Settings className="w-4 h-4 me-2" />
                    {t('dashboard_settings')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings/privacy')} data-testid="goto-privacy">
                    <Shield className="w-4 h-4 me-2" />
                    Confidentialité
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 me-2" />
                    {t('nav_logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className={`hidden md:flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Button variant="ghost" onClick={() => navigate('/login')} data-testid="login-btn">
                  {t('nav_login')}
                </Button>
                <Button onClick={() => navigate('/register')} data-testid="register-btn">
                  {t('nav_register')}
                </Button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200" data-testid="mobile-menu">
            <nav className="flex flex-col gap-2">
              <Link
                to="/"
                className="px-4 py-2 text-slate-600 hover:bg-muted rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav_home')}
              </Link>
              <Link
                to="/professionals"
                className="px-4 py-2 text-slate-600 hover:bg-muted rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav_professionals')}
              </Link>
              <Link
                to="/pricing"
                className="px-4 py-2 text-slate-600 hover:bg-muted rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav_pricing')}
              </Link>
              {isSuperAdmin && (
                <Link
                  to="/admin"
                  className="px-4 py-2 text-amber-600 hover:bg-amber-50 rounded-lg flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-nav-admin"
                >
                  <Shield className="w-4 h-4" />
                  Admin
                </Link>
              )}
              {!isAuthenticated && (
                <>
                  <hr className="my-2 border-slate-200" />
                  <Link
                    to="/login"
                    className="px-4 py-3 text-blue-600 font-semibold hover:bg-blue-50 rounded-lg flex items-center gap-2"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-nav-login"
                  >
                    <User className="w-4 h-4" />
                    {t('nav_login')}
                  </Link>
                  <Button
                    className="mx-4 justify-center"
                    onClick={() => {
                      navigate('/register');
                      setMobileMenuOpen(false);
                    }}
                  >
                    {t('nav_register')}
                  </Button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>

      {/* Mobile Bottom Navigation Bar - Fixed */}
      {!isAuthenticated && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-50">
          <div className="flex items-center justify-around h-16">
            <Link 
              to="/" 
              className="flex flex-col items-center justify-center text-slate-600 hover:text-primary px-3 py-2"
              data-testid="mobile-bottom-home"
            >
              <Home className="w-5 h-5" />
              <span className="text-xs mt-1">{t('nav_home')}</span>
            </Link>
            <Link 
              to="/professionals" 
              className="flex flex-col items-center justify-center text-slate-600 hover:text-primary px-3 py-2"
              data-testid="mobile-bottom-search"
            >
              <Search className="w-5 h-5" />
              <span className="text-xs mt-1">{t('nav_professionals')}</span>
            </Link>
            <Link 
              to="/pricing" 
              className="flex flex-col items-center justify-center text-slate-600 hover:text-primary px-3 py-2"
              data-testid="mobile-bottom-pricing"
            >
              <CreditCard className="w-5 h-5" />
              <span className="text-xs mt-1">{t('nav_pricing')}</span>
            </Link>
            <Link 
              to="/login" 
              className="flex flex-col items-center justify-center text-blue-600 font-semibold hover:text-blue-700 px-3 py-2"
              data-testid="mobile-bottom-login"
            >
              <User className="w-5 h-5" />
              <span className="text-xs mt-1">{t('nav_login')}</span>
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
